<template>
  <template v-if="showNewRecipientModal">
    <Teleport to="#modal">
      <TheModal
        title="Creating new recipient"
        @closeModal="closeModal"
        :open="showNewRecipientModal"
        :showModalHeader="true"
        :closeOnClickOutside="false"
      >
        <NewRecipientForm
          @closeModal="closeModal"
          @handleNewRecipient="handleNewContact"
        ></NewRecipientForm>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="showDeleteModal">
    <Teleport to="#modal">
      <TheModal
        :showModalHeader="true"
        :showModalBody="true"
        :closeOnClickOutside="false"
        title="Delete this recipient?"
        @closeModal="closeModal"
        :open="showDeleteModal"
      >
        <ModalBody>
          <DeleteData
            :recipients="recipientIdToDelete"
            @closeModal="closeModal"
            @confirmDelete="confirmDelete()"
          ></DeleteData>
        </ModalBody>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="checkingIfUserHasRecipients">
    <div
      class="tw-flex tw-justify-center tw-items-center tw-w-full tw-max-w-5xl tw-mx-auto"
    >
      <PageSection :fullWidth="true">
        <PulseAnimation>
          <DataTableSkeleton></DataTableSkeleton>
        </PulseAnimation>
      </PageSection>
    </div>
  </template>
  <template v-else>
    <TheCard class="tw-w-full">
      <template v-if="userHasRecipients">
        <CardHeader
          title="Recipients"
          :showTitle="dashboardView"
          :showActions="true"
          :useSearchBar="!dashboardView && userHasRecipients ? true : false"
        >
          <template #card-search-bar>
            <div class="tw-max-w-xl tw-w-full">
              <div class="tw-relative tw-rounded-md tw-shadow-sm tw-w-full">
                <div
                  class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
                >
                  <b-icon-search
                    class="tw-h-5 tw-w-5 tw-text-gray-400"
                    aria-hidden="true"
                  ></b-icon-search>
                </div>
                <input
                  @keyup.enter="handleSearchQuery()"
                  v-model="searchQuery"
                  type="text"
                  name="recipientSearch"
                  id="recipientSearch"
                  class="tw-block tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-pl-10 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-slate-900 focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-600 tw-text-sm tw-leading-6"
                  placeholder="Search by recipient name or email"
                />
              </div>
            </div>
          </template>
          <template #card-header-actions>
            <template v-if="dashboardView">
              <TheLink to="/recipients" :isRouterLink="true">
                <template #text>View all recipients</template>
              </TheLink>
            </template>
            <template v-if="campaignView && userHasRecipients">
              <div class="tw-flex tw-flex-col tw-gap-4 tw-items-center">
                <TheButton
                  type="button"
                  :hasLeadingIcon="true"
                  variant="primary"
                  size="small"
                  @click="toggleNewRecipientModal"
                >
                  <template #text>Create recipient</template>
                  <template #leading-icon
                    ><b-icon-plus-lg></b-icon-plus-lg
                  ></template>
                </TheButton>
              </div>
            </template>
          </template>
        </CardHeader>
      </template>
      <CardBody>
        <template v-if="recipientsAreLoading">
          <PulseAnimation>
            <DataTableSkeleton></DataTableSkeleton>
          </PulseAnimation>
        </template>
        <template v-else>
          <!-- if userHasRecipients show recipient list -->
          <template v-if="userHasRecipients && count">
            <div
              class="tw-overflow-y-auto tw-min-h-[500px] tw-max-h-[500px] tw-w-full"
            >
              <table
                class="tw-min-w-full tw-divide-stone-300 tw-sticky tw-top tw-z-1"
              >
                <thead
                  class="tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-stone-300"
                >
                  <tr>
                    <th
                      scope="col"
                      class="tw-min-w-20 tw-py-3.5 tw-px-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      <template v-if="selectedRecipients.length > 0">
                        <TheButton
                          type="button"
                          variant="critical"
                          :disabled="recipientsAreLoading"
                          @click="handleButtonClick"
                        >
                          {{ recipientsAreLoading ? "Loading..." : "Delete" }}
                        </TheButton>
                      </template>
                      <template v-else> Name </template>
                    </th>
                    <th
                      scope="col"
                      class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      Email
                    </th>
                    <template v-if="!dashboardView">
                      <th
                        scope="col"
                        class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        Phone number
                      </th>
                      <th
                        scope="col"
                        class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        Company
                      </th>
                    </template>

                    <th
                      scope="col"
                      class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      Lifecycle Stage
                    </th>
                    <template v-if="!dashboardView">
                      <th
                        scope="col"
                        class="tw-px-3 tw-py-3.5 tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-z-1 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        <span class="tw-sr-only">Edit</span>
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody
                  class="tw-divide-y tw-divide-gray-200 tw-bg-white tw-min-h-[500px]"
                >
                  <tr
                    v-for="recipient in recipientData"
                    :key="recipient.id"
                    :class="[
                      selectedRecipients.includes(recipient.id) && 'bg-gray-50',
                    ]"
                  >
                    <td
                      :class="[
                        'tw-py-4 tw-px-3 ',
                        selectedRecipients.includes(recipient.id)
                          ? 'tw-text-blue-600'
                          : 'tw-text-gray-900',
                      ]"
                    >
                      <TheLink
                        variant="tertiary"
                        :url="{
                          name: 'RecipientDetails',
                          params: { recipientId: recipient.id },
                        }"
                        :isRouterLink="true"
                      >
                        <template #text>
                          <template
                            v-if="recipient?.firstName && recipient?.lastName"
                          >
                            {{
                              recipient?.firstName + " " + recipient?.lastName
                            }}
                          </template>
                          <template v-else>N/A</template>
                        </template>
                        <template #leading-icon
                          ><b-icon-plus-lg></b-icon-plus-lg>
                        </template>
                      </TheLink>
                      <div class="sm:tw-hidden tw-flex tw-flex-col">
                        <p class="tw-p-0 tw-m-0 tw-text-sm">
                          {{ recipient?.contact?.email || "---" }}
                        </p>
                      </div>
                    </td>
                    <td
                      class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                    >
                      {{ recipient?.contact?.email || "---" }}
                      <template v-if="campaignView">
                        <template
                          v-if="recipient?.contact?.metadata?.lifecycleStage"
                        >
                          <p class="tw-p-0 tw-m-0 tw-text-sm">
                            {{ recipient?.contact?.metadata?.lifecycleStage }}
                          </p>
                        </template>
                        <template v-if="recipient?.contact?.company">
                          <p class="tw-p-0 tw-m-0 tw-text-sm">
                            {{ recipient?.contact?.company }}
                          </p>
                        </template>
                      </template>
                    </td>

                    <template v-if="!dashboardView">
                      <td
                        class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                      >
                        {{ recipient?.contact?.phone || "---" }}
                      </td>
                      <td
                        class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                      >
                        {{ recipient?.contact?.company || "---" }}
                      </td>
                    </template>

                    <td
                      class="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                    >
                      {{
                        recipient?.contact?.metadata?.lifecycleStage || "---"
                      }}
                    </td>

                    <template v-if="!dashboardView">
                      <td
                        class="tw-py-4 tw-pl-3 tw-pr-4 tw-text-right tw-text-sm tw-font-medium sm:tw-pr-3"
                      >
                        <div class="tw-flex tw-justify-end">
                          <template v-if="campaignView">
                            <TheButton
                              variant="primary"
                              @click="setCampaignRecipient(recipient)"
                              size="small"
                            >
                              <template #text>Select</template>
                            </TheButton>
                          </template>
                          <template v-else>
                            <div class="tw-flex tw-justify-end">
                              <DropdownMenu
                                :items="getDropdownItems(recipient)"
                                :iconDropdown="true"
                              >
                                <template #icon>
                                  <div
                                    class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-slate-50 tw-flex tw-items-center tw-justify-center hover:tw-bg-slate-100"
                                  >
                                    <b-icon-three-dots-vertical
                                      class="tw-text-black tw-z-0"
                                    ></b-icon-three-dots-vertical>
                                  </div>
                                </template>
                              </DropdownMenu>
                            </div>
                          </template>
                        </div>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <!-- if userHasRecipients AND query count is 0 show recipient message -->
          <template v-if="userHasRecipients && !count">
            <div
              class="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[500px] tw-min-w-96 tw-p-10"
            >
              <img
                style="max-height: 150px; width: auto"
                src="../../assets/svg/illustrations-test/14_no_query_results.png"
                alt="Image Description"
              />
              <h1 class="tw-text-2xl tw-m-0">No exact matches</h1>
              <p class="tw-m-0 tw-text-lg">
                Try updating your search/filters or
                <a
                  class="tw-font-bold tw-cursor-pointer"
                  @click="resetFilters()"
                  >click here</a
                >
                to reset.
              </p>
            </div>
          </template>
          <!-- if !userHasRecipients show message -->
          <template v-if="!userHasRecipients">
            <div
              class="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[500px]"
            >
              <EmptyState emptyState="recipients"></EmptyState>
              <div class="tw-flex tw-flex-col tw-gap-4 tw-items-center">
                <TheButton
                  type="button"
                  :hasLeadingIcon="true"
                  variant="primary"
                  size="large"
                  @click="toggleNewRecipientModal"
                >
                  <template #text>Create recipient</template>
                  <template #leading-icon
                    ><b-icon-plus-lg></b-icon-plus-lg
                  ></template>
                </TheButton>
                <TheLink
                  type="button"
                  variant="tertiary"
                  size="large"
                  :isRouterLink="true"
                  to="/recipients/import"
                >
                  <template #text>Import CSV</template>
                </TheLink>
              </div>
            </div>
          </template>
        </template>
      </CardBody>
      <template v-if="userHasRecipients && !dashboardView">
        <CardFooter>
          <!-- Pagination -->
          <nav
            class="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200"
            aria-label="Pagination"
          >
            <div class="tw-hidden sm:tw-block">
              <p class="tw-text-sm tw-text-gray-700 tw-m-0">
                <span class="tw-font-bold">{{ count }}</span> records
              </p>
            </div>
            <div
              class="tw-flex tw-flex-1 tw-justify-between tw-gap-x-3 sm:tw-justify-end"
            >
              <TheButton
                class="disabled:tw-bg-slate-200 disabled:tw-cursor-auto"
                :disabled="this.currentPage === 1"
                @click="previousPage()"
                variant="tertiary"
                size="small"
              >
                <template #text
                  ><b-icon-chevron-left></b-icon-chevron-left
                ></template>
              </TheButton>
              <TheButton
                class="disabled:tw-bg-slate-200"
                :disabled="this.isLastPage"
                @click="nextPage()"
                variant="tertiary"
                size="small"
              >
                <template #text
                  ><b-icon-chevron-right></b-icon-chevron-right
                ></template>
              </TheButton>
            </div>
          </nav>
        </CardFooter>
      </template>
    </TheCard>
  </template>
</template>

<script>
import TheCard from "../../components-v2/ui/layout/TheCard.vue";
import CardHeader from "../../components-v2/ui/layout/CardHeader.vue";
import CardBody from "../../components-v2/ui/layout/CardBody.vue";
import CardFooter from "../../components-v2/ui/layout/CardFooter.vue";
import TheModal from "../../components-v2/ui/modals/TheModal.vue";
import NewRecipientForm from "../../components-v2/ui/modals/NewRecipientForm.vue";
import TheButton from "../../components-v2/ui/actions/TheButton.vue";
import TheLink from "../../components-v2/ui/actions/TheLink.vue";
import EmptyState from "../../components-v2/ui/feedback/EmptyState.vue";
import DropdownMenu from "../../components-v2/ui/forms/DropdownMenu.vue";
import DeleteData from "../ui/modals/DeleteData.vue";
import PageSection from "../../components-v2/ui/layout/PageSection.vue";
import DataTableSkeleton from "../../components-v2/ui/skeletons/DataTableSkeleton.vue";
import PulseAnimation from "../../components-v2/ui/feedback/PulseAnimation.vue";
import ModalBody from "../../components-v2/ui/layout/ModalBody.vue";

export default {
  name: "RecipientsIndexTable",
  components: {
    DeleteData,
    NewRecipientForm,
    TheModal,
    DropdownMenu,
    TheLink,
    EmptyState,
    CardBody,
    TheCard,
    CardHeader,
    TheButton,
    CardFooter,
    PageSection,
    DataTableSkeleton,
    PulseAnimation,
    ModalBody,
  },
  emits: ["setCampaignRecipient"],
  props: {
    campaignView: {
      type: Boolean,
      default: false,
    },
    dashboardView: {
      type: Boolean,
      default: false,
    },
    isModalVisible: {
      type: Boolean,
      default: false,
    },
    sharedCampaignData: {
      type: Array,
      default: () => [],
    },
    totalRecipientsCount: {
      type: Number,
      default: 1,
      required: false,
    },
  },
  watch: {
    isModalVisible(newVal) {
      if (newVal) {
        this.toggleNewRecipientModal();
      }
    },
  },
  created() {
    /** load data */

    if (!this.dashboardView) {
      this.checkIfUserHasRecipients();
    } else {
      this.recipientsAreLoading = false;
      this.checkingIfUserHasRecipients = false;
      if (this.totalRecipientsCount) {
        this.userHasRecipients = true;
      }
      this.count = this.totalRecipientsCount;
    }
  },
  data() {
    return {
      showDeleteModal: false,
      filter: {
        active: false,
        matchExists: false,
        count: 0,
      },
      count: 0,
      pageSize: 25,
      currentPage: 1,
      searchQuery: "",
      sortedValue: "lastName",
      showNewRecipientModal: false,
      recipientsAreLoading: false,
      isAddingRecipient: false,
      userHasRecipients: false,
      checkingIfUserHasRecipients: false,
      recipients: [],
      selectedRecipients: [],
      recipientIdToDelete: null,
      recipientIsReadyToSave: false,
      newRecipient: {},
    };
  },
  computed: {
    recipientData() {
      return this.dashboardView ? this.sharedCampaignData : this.recipients;
    },
    isLastPage() {
      return (
        this.count <= this.pageSize ||
        this.currentPage === Math.ceil(this.count) ||
        this.recipients.length < this.pageSize ||
        this.count / this.currentPage === this.pageSize
      );
    },
    userId() {
      return this?.$store?.state?.auth?.user?._id;
    },
    userClient() {
      return this?.$store?.state?.auth?.user?.client;
    },
    notificationTypes() {
      // Access namespaced getter
      return this.$store.getters["alerts/getAlertTypes"];
    },
  },
  methods: {
    /** hover */
    getDropdownItems(recipient) {
      return [
        {
          label: "View details",
          isRouterLink: true,
          to: `/recipient/${recipient.id}`,
        },
        {
          label: "Delete",
          isDeleteAction: true, // This item is a delete action
          action: () => this.toggleDeleteModal(recipient),
        },
      ];
    },

    /** handle delete */
    async confirmDelete() {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      const recipientIdToDelete = this.recipientIdToDelete[0].id;

      try {
        const response = await api.deleteRecipients(null, {
          filter: {
            _id: recipientIdToDelete,
          },
        });
        console.log("delete successful", recipientIdToDelete);
        this.showDeleteModal = false;
        this.handleSuccess(response, "deleteRecipients");
      } catch (error) {
        this.showDeleteModal = false;
        console.log("recipient was NOT deleted", error);
        this.handleError(error);
      }
    },
    toggleDeleteModal(recipientId) {
      this.recipientIdToDelete = [recipientId];
      this.showDeleteModal = !this.showDeleteModal;
    },
    closeModal() {
      this.showDeleteModal = false;
      this.showNewRecipientModal = false;
      this.$emit("update:isModalVisible", false);
    },

    /** load data */
    async checkIfUserHasRecipients(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.currentPage = page === undefined ? this.currentPage : page;
      this.checkingIfUserHasRecipients = true;

      let requestBody = {};

      if (!this.isBoldXchangeAdmin) {
        requestBody = {
          "metadata.giftingLeads": { $in: [this.userId] },
        };
      }
      let selection = "firstName lastName contact metadata";

      // if search query is truthy
      if (this.searchQuery !== "") {
        requestBody.$or = [
          { firstName: { $regex: this.searchQuery } },
          { lastName: { $regex: this.searchQuery } },
          { "contact.email": { $regex: this.searchQuery } },
          { "contact.phone": { $regex: this.searchQuery } },
          { "contact.company": { $regex: this.searchQuery } },
          { "contact.title": { $regex: this.searchQuery } },
          { "contact.metadata.lifecycleStage": { $regex: this.searchQuery } },
        ];
      }

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "contact",
      };

      try {
        const response = await api.listRecipients(queryParameters, {
          filter: requestBody,
          selection: selection,
        });
        const count = response.data.result.count;
        this.count = response.data.result.count;
        this.userHasRecipients = count;
        this.recipients = response.data.result.records;
        this.checkingIfUserHasRecipients = false;
      } catch (error) {
        this.checkingIfUserHasRecipients = false;
        console.log("recipients did not load", error);
      }
    },

    /** paginate */
    async nextPage(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;
      if (page === undefined) {
        this.currentPage++;
      } else {
        this.currentPage = page;
      }
      // console.log("currentPage", this.currentPage);

      let requestBody = {};

      if (!this.isBoldXchangeAdmin) {
        requestBody = {
          "metadata.giftingLeads": { $in: [this.userId] },
        };
      }
      let selection = "firstName lastName contact metadata";

      // if search query is truthy
      if (this.searchQuery !== "") {
        requestBody.$or = [
          { firstName: { $regex: this.searchQuery } },
          { lastName: { $regex: this.searchQuery } },
          { "contact.email": { $regex: this.searchQuery } },
          { "contact.phone": { $regex: this.searchQuery } },
          { "contact.company": { $regex: this.searchQuery } },
          { "contact.title": { $regex: this.searchQuery } },
          { "contact.metadata.lifecycleStage": { $regex: this.searchQuery } },
        ];
      }

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "contact",
      };

      try {
        const response = await api.listRecipients(queryParameters, {
          filter: requestBody,
          selection: selection,
        });

        this.recipientsAreLoading = false;
        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.recipientsAreLoading = false;
        console.log("recipients did not load", error);
      }
    },
    async previousPage(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;
      if (page === undefined) {
        this.currentPage--;
      } else {
        this.currentPage = page;
      }
      // console.log("currentPage", this.currentPage);

      let requestBody = {};

      if (!this.isBoldXchangeAdmin) {
        requestBody = {
          "metadata.giftingLeads": { $in: [this.userId] },
        };
      }
      let selection = "firstName lastName contact metadata";

      // if search query is truthy
      if (this.searchQuery !== "") {
        requestBody.$or = [
          { firstName: { $regex: this.searchQuery } },
          { lastName: { $regex: this.searchQuery } },
          { "contact.email": { $regex: this.searchQuery } },
          { "contact.phone": { $regex: this.searchQuery } },
          { "contact.company": { $regex: this.searchQuery } },
          { "contact.title": { $regex: this.searchQuery } },
          { "contact.metadata.lifecycleStage": { $regex: this.searchQuery } },
        ];
      }

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "contact",
      };

      try {
        const response = await api.listRecipients(queryParameters, {
          filter: requestBody,
          selection: selection,
        });

        this.recipientsAreLoading = false;
        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.recipientsAreLoading = false;
        console.log("recipients did not load", error);
      }
    },

    /** query data */
    async loadRecipients(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;

      this.currentPage = page === undefined ? this.currentPage : page;

      let requestBody = {};

      if (!this.isBoldXchangeAdmin) {
        requestBody = {
          "metadata.giftingLeads": { $in: [this.userId] },
        };
      }

      let selection = "firstName lastName contact metadata";

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "contact",
      };

      try {
        const response = await api.listRecipients(queryParameters, {
          filter: requestBody,
          selection: selection,
        });

        this.recipientsAreLoading = false;
        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.recipientsAreLoading = false;
        console.log("recipients did not load", error);
      }
    },

    /** search query */
    createSearchQuery() {
      if (this.searchQuery === "") return {};

      const searchValues = this.searchQuery.split(" ");
      const userFields = ["firstName", "lastName", "contact.email"];

      const searchConditions = searchValues.map((value) => ({
        $or: userFields.map((field) => ({
          [field]: { $regex: value, $options: "i" },
        })),
      }));

      return { $and: searchConditions };
    },
    async fetchRecipients(query, page) {
      const api = await this.getBoldClient();
      const selection = "firstName lastName contact metadata";

      const queryParameters = {
        skip: (page - 1) * this.pageSize,
        limit: this.pageSize,
        sort: "name",
        expand: "contact",
      };

      try {
        const response = await api.listRecipients(queryParameters, {
          filter: query,
          selection: selection,
        });

        return {
          records: response.data.result.records,
          count: response.data.result.count,
        };
      } catch (error) {
        console.log("Recipients did not load", error);
        throw error; // Re-throw error for handling by caller
      }
    },
    // Main method to get recipients by search query
    async getRecipientsBySearchQuery(page) {
      this.recipientsAreLoading = true;
      this.currentPage = page === undefined ? this.currentPage : page;
      let query = {};

      if (!this.isBoldXchangeAdmin) {
        query["metadata.giftingLeads"] = { $in: [this.userId] };
      }

      query = this.createSearchQuery();

      try {
        const { records, count } = await this.fetchRecipients(
          query,
          this.currentPage
        );

        this.recipientsAreLoading = false;
        this.recipients = records;
        this.count = count;
      } catch (error) {
        this.recipientsAreLoading = false;
      }
    },

    /** handlers */
    handleSort(column) {
      this.currentPage = 1;
      this.deactivateQuickEdit();
      if (this.sortedValue === column) {
        this.sortedValue = `-${column}`;
        return this.loadRecipients();
      } else {
        this.sortedValue = column;
        return this.loadRecipients();
      }
    },
    handleSelectContact(recipient) {
      this.isAddingRecipient = false;
      this.$emit("handleSelectedContact", recipient);
    },
    setCampaignRecipient(recipient) {
      // Method to send the campaign with the recipient provided ID
      this.$emit("setCampaignRecipient", recipient);
      console.log(recipient);
    },
    handleSearchQuery() {
      this.getRecipientsBySearchQuery();
    },
    resetFilters() {
      this.searchQuery = "";
      this.currentPage = 1;
      this.loadRecipients();
    },
    reloadList() {
      const page = this.currentPage;
      if (this.searchQuery !== "") {
        this.getRecipientsBySearchQuery(page);
      } else {
        this.checkIfUserHasRecipients(page);
        // this.loadRecipients(page);
      }
    },

    /** quick edits */
    activateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to true
      this.$store.dispatch("orders/activateQuickEdit");
    },
    deactivateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to false
      this.$store.dispatch("orders/deactivateQuickEdit");
    },

    /** modals */
    toggleNewRecipientModal() {
      this.showNewRecipientModal = !this.showNewRecipientModal;
    },
    handleNewContact(recipient) {
      // console.log("recipient added", recipient);
      this.showNewRecipientModal = false;
      this.handleSuccess(recipient, "handleNewRecipient");
      this.$emit("handleSelectedContact", recipient);
    },

    /** handle errors */
    handleSuccess(response, api) {
      console.log("new recipient successful: ", response);
      console.log("api: ", api);

      if (this.campaignView) {
        this.$emit("setCampaignRecipient", response);
        return;
      }

      if (api === "handleNewRecipient") {
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipient successfully created.",
          duration: 4000,
          id: Date.now(),
        });
      } else if (api === "deleteRecipients") {
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipient successfully deleted.",
          duration: 4000,
          id: Date.now(),
        });
      } else {
        this.$emit("handleSelectedContact", response);
        this.showNewRecipientModal = false;
      }

      this.reloadList();
    },
    handleErrorFromAPI(error) {
      let errorMessage = "Something went wrong. Please try again."; // Default message
      if (error?.response) {
        if (
          error?.response?.data &&
          error?.response?.data?.error &&
          error?.response?.data?.error?.source?.errors
        ) {
          const sourceErrors = error.response.data.error.source.errors;
          const firstErrorKey = Object.keys(sourceErrors)[0];
          errorMessage = sourceErrors[firstErrorKey].message;
        } else {
          errorMessage =
            error?.response?.data?.message || "An unknown error occurred.";
        }
      } else if (error?.request) {
        errorMessage =
          "No response was received from the server. Please check your internet connection.";
      } else {
        errorMessage =
          "An error occurred setting up the request that prevented it from being sent. Please try again.";
      }

      // Dispatch the notifyError action to the Vuex store
      this.$store.dispatch("notifications/notifyError", errorMessage);

      this.reloadList();

      console.error(errorMessage);
    },
  },
};
</script>
